import { AsyncPipe, NgIf } from '@angular/common';
import { Component, Input } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { CanDisable, mixinDisabled } from '@angular/material/core';
import { MatLegacyDialogModule } from '@angular/material/legacy-dialog';
import {
  MatLegacySnackBar as MatSnackBar,
  MatLegacySnackBarModule as MatSnackBarModule,
} from '@angular/material/legacy-snack-bar';
import _ from 'lodash';
import { Observable, filter, switchMap, take, tap } from 'rxjs';
import {
  getAvailableLinesJoints,
  getAvailablePanelsJoints,
  recalculateLineJoint,
  recalculatePanelJoint,
} from '../../../helpers/countertop.utils';
import {
  Countertop,
  CountertopLinesJointTypeEnum,
  CuttingMaterial,
  GShapedCountertop,
  GShapedCountertopSubtypeEnum,
  PShapedCountertop,
  PShapedCountertopSubtypeEnum,
  SimpleCountertop,
} from '../../../models/cutting/public-api';
import { ClampJoint } from '../../../models/user/cutting-settings.model';
import { LegacyDialogsService } from '../../../services/legacy-dialogs.service';
import { CountertopSettingsStateService } from '../countertop-settings-state.service';
import { ItemCardComponent } from '../item-card/item-card.component';
import { CountertopTypeSelectDialogComponent } from './countertop-type-select-dialog/countertop-type-select-dialog.component';

export class CountertopTypeSelectBase {}
export const _CountertopTypeSelectMixinBase = mixinDisabled(CountertopTypeSelectBase);

@Component({
  selector: 'countertop-type-select',
  standalone: true,
  templateUrl: 'countertop-type-select.component.html',
  imports: [
    ItemCardComponent,
    NgIf,
    AsyncPipe,
    CountertopTypeSelectDialogComponent,
    MatLegacyDialogModule,
    MatSnackBarModule,
  ],
})
export class CountertopTypeSelectComponent extends _CountertopTypeSelectMixinBase implements CanDisable {
  pShaperTypes = PShapedCountertopSubtypeEnum;
  gShaperTypes = GShapedCountertopSubtypeEnum;

  material$: Observable<CuttingMaterial>;
  selectDialog$: Observable<Countertop>;

  data$: Observable<any>;
  countertop: Countertop;

  @Input() panelConnectors: ClampJoint[] = [];

  constructor(
    private snackBar: MatSnackBar,
    private countertopSettingsService: CountertopSettingsStateService,
    private dialogs: LegacyDialogsService,
    private _bottomSheet: MatBottomSheet
  ) {
    super();

    this.data$ = this.countertopSettingsService.countertop$.pipe(
      tap((p: Countertop) => {
        this.countertop = p;
      })
    );
  }

  openSelectDialog(): void {
    this.selectDialog$ = this.countertopSettingsService.material$.pipe(
      take(1),
      filter(material => {
        if (!material) {
          this.snackBar.open($localize`Выберите материал`);
        }
        return !!material;
      }),
      switchMap(material =>
        this.dialogs.component<CuttingMaterial, Countertop>(CountertopTypeSelectDialogComponent, material, null)
      ),
      filter(res => !!res),
      tap(c => {
        if (!_.isEmpty(c.linesJoints)) {
          c.linesJoints.forEach(item => {
            if (item.type === CountertopLinesJointTypeEnum.None) {
              return;
            }

            const availableConnectors = getAvailableLinesJoints(c, item, this.panelConnectors);

            if (availableConnectors.length === 0) {
              item.connector = undefined;
              this.snackBar.open($localize`Не найдены доступные стяжки для соединения столешниц`);
              return;
            }

            recalculateLineJoint(item, availableConnectors[0]);
          });
        }

        c.lines.forEach(l => {
          l.panelsJoints.length = 0;

          for (let i = 1; i < l.panels.length; i++) {
            const panel1 = l.panels[i - 1];
            const panel2 = l.panels[i];
            l.panelsJoints.push({ panel1, panel2, connector: undefined });

            const availableConnectors = getAvailablePanelsJoints(
              c,
              l.panelsJoints[l.panelsJoints.length - 1],
              this.panelConnectors
            );

            if (availableConnectors.length === 0) {
              this.snackBar.open($localize`Не найдены доступные стяжки для соединения панелей`);
              continue;
            }

            recalculatePanelJoint(c, l.panelsJoints[l.panelsJoints.length - 1], availableConnectors[0]);
          }
        });

        this.countertopSettingsService.updateCountertop(c);

        if (c.linesJoints?.some(j => j.type !== CountertopLinesJointTypeEnum.None) ?? false)
          this.snackBar.open('⚠ ' + $localize`Стяжки пересчитаны для нового материала`);
      })
    );
  }

  getDescription(): string {
    return this.countertop instanceof PShapedCountertop
      ? $localize`П-образная`
      : this.countertop instanceof GShapedCountertop
      ? $localize`Г-образная`
      : this.countertop instanceof SimpleCountertop
      ? $localize`Простая`
      : $localize`Выберите тип столешницы`;
  }

  getIconLink(): string {
    if (this.countertop instanceof PShapedCountertop) {
      switch (this.countertop.subtype) {
        case PShapedCountertopSubtypeEnum.FullLengthTop:
          return 'assets/images/countertop-types/pShapedFullLengthTop.svg';

        case PShapedCountertopSubtypeEnum.WithFullWidthSides:
          return 'assets/images/countertop-types/pShapedWithFullWidthSides.svg';

        case PShapedCountertopSubtypeEnum.WithFullWidthLeftSide:
          return 'assets/images/countertop-types/pShapedWithFullWidthLeftSide.svg';

        case PShapedCountertopSubtypeEnum.WithFullWidthRightSide:
          return 'assets/images/countertop-types/pShapedWithFullWidthRightSide.svg';

        default:
          return '';
      }
    }

    if (this.countertop instanceof GShapedCountertop) {
      switch (this.countertop.subtype) {
        case GShapedCountertopSubtypeEnum.FullLengthTopWithLeftSide:
          return 'assets/images/countertop-types/gShapedFullLengthTopWithLeftSide.svg';

        case GShapedCountertopSubtypeEnum.FullLengthTopWithRightSide:
          return 'assets/images/countertop-types/gShapedFullLengthTopWithRightSide.svg';

        case GShapedCountertopSubtypeEnum.WithFullWidthLeftSide:
          return 'assets/images/countertop-types/gShapedWithFullWidthLeftSide.svg';

        case GShapedCountertopSubtypeEnum.WithFullWidthRightSide:
          return 'assets/images/countertop-types/gShapedWithFullWidthRightSide.svg';

        default:
          return '';
      }
    }

    if (this.countertop instanceof SimpleCountertop) {
      return 'assets/images/countertop-types/simple.svg';
    }

    return '';
  }
}
